import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, ChevronRight, ChevronLeft, MapPin, Shield, Users, Wrench, UserCheck, ListTodo, Search, Info, Loader2, ClipboardCheck } from 'lucide-react';
import api, { workingSiteAPI } from '../../../api.js';
import { apiRequest } from '../../../api/core.js';
import { insuranceModuleAPI } from '../../../api/insurance.js';

// ── Action definitions ──────────────────────────────────────────────
const ACTIONS = [
  { id: 'site',       icon: MapPin,        label: 'Affecter à un site',      desc: 'Assigner N véhicules à un site de travail',    color: 'blue' },
  { id: 'insurance',  icon: Shield,        label: 'Affecter à un contrat',   desc: 'Rattacher N véhicules à un contrat assurance',  color: 'indigo' },
  { id: 'driver',     icon: UserCheck,     label: 'Affecter un conducteur',  desc: 'Mapper chaque véhicule à un conducteur (1:1)',  color: 'emerald' },
  { id: 'team',       icon: Users,         label: 'Affecter à une équipe',   desc: 'Ajouter véhicule + conducteur à une équipe',    color: 'purple' },
  { id: 'tools',      icon: Wrench,        label: 'Affecter des outils',     desc: 'Assigner des outils à un véhicule',             color: 'amber' },
  { id: 'inspection', icon: ClipboardCheck,label: 'Inspection groupée',      desc: 'Inspection multi-véhicule avec scoring',        color: 'cyan' },
];

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',   text: 'text-blue-700',    icon: 'text-blue-500',    gradient: 'from-blue-500 to-blue-600' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200', text: 'text-indigo-700',  icon: 'text-indigo-500',  gradient: 'from-indigo-500 to-indigo-600' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200',text: 'text-emerald-700', icon: 'text-emerald-500', gradient: 'from-emerald-500 to-emerald-600' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-200', text: 'text-purple-700',  icon: 'text-purple-500',  gradient: 'from-purple-500 to-purple-600' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',  text: 'text-amber-700',   icon: 'text-amber-500',   gradient: 'from-amber-500 to-amber-600' },
  cyan:    { bg: 'bg-cyan-50',    border: 'border-cyan-200',   text: 'text-cyan-700',    icon: 'text-cyan-500',    gradient: 'from-cyan-500 to-cyan-600' },
};

// Fallback if DB models not loaded
const FALLBACK_COVERAGES = [
  { id: 'tous_risques', name: 'Tous Risques', level: 'tous_risques' },
  { id: 'tiers_plus', name: 'Tiers+', level: 'tiers_plus' },
  { id: 'tiers', name: 'Tiers Simple', level: 'tiers_simple' },
];

function BulkActionsModal({ onClose, onSuccess, onMessage, vehicles = [], drivers = [], sites = [], onOpenInspection }) {
  // ── Which action is selected ──────────────────────────────────────
  const [activeAction, setActiveAction] = useState(null);

  // ── Shared A×B=C state ────────────────────────────────────────────
  const [selected, setSelected] = useState(new Set());
  const [done, setDone] = useState([]);
  const [searchA, setSearchA] = useState('');

  // ── Reference data (loaded on demand) ─────────────────────────────
  const [contracts, setContracts] = useState([]);
  const [coverageModels, setCoverageModels] = useState([]);
  const [loadedSites, setLoadedSites] = useState(null); // null = use prop
  const [teams, setTeams] = useState([]);
  const [tools, setTools] = useState([]);
  const [loadingRef, setLoadingRef] = useState(false);
  // Globally insured vehicle IDs — a vehicle with any active vehicle_insurance
  // record on any contract is excluded from the picker. To move a vehicle
  // from contract A to B, terminate A first (cleaner than a double-assign).
  const [globallyInsuredVehicleIds, setGloballyInsuredVehicleIds] = useState(new Set());

  // ── Action-specific Column B state ────────────────────────────────
  const [siteId, setSiteId] = useState('');
  const [assignType, setAssignType] = useState('permanent');
  const [effectDate, setEffectDate] = useState(new Date().toISOString().split('T')[0]);
  const [contractId, setContractId] = useState('');

  // Load globally insured vehicles when entering insurance action
  useEffect(() => {
    if (activeAction !== 'insurance') {
      setGloballyInsuredVehicleIds(new Set());
      return;
    }
    const loadInsured = async () => {
      try {
        const res = await api.vehicleInsurance.getAll();
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setGloballyInsuredVehicleIds(new Set(
          rows.filter(r => !r.removed_at).map(r => r.vehicle_id)
        ));
      } catch (err) {
        console.error('Error loading insured vehicles globally:', err);
      }
    };
    loadInsured();
  }, [activeAction]);
  const [coverage, setCoverage] = useState('tous_risques');
  const [driverMap, setDriverMap] = useState({}); // vehicleId → driverId
  const [teamId, setTeamId] = useState('');
  const [selectedTools, setSelectedTools] = useState(new Set());

  // ── Confirmation / sending ────────────────────────────────────────
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // ── Load reference data when action changes ───────────────────────
  useEffect(() => {
    if (!activeAction) return;
    const load = async () => {
      setLoadingRef(true);
      try {
        if (activeAction === 'insurance') {
          const [cRes, mRes] = await Promise.all([
            api.insurance.getAll({ status: 'active' }),
            insuranceModuleAPI.getModels(),
          ]);
          if (cRes.ok) setContracts(cRes.data || []);
          if (mRes.ok && mRes.data?.length) setCoverageModels(mRes.data);
        }
        if (activeAction === 'site') {
          const res = await workingSiteAPI.getAll();
          if (res.ok || res.data) setLoadedSites(res.data || []);
        }
        if (activeAction === 'team') {
          const res = await api.team.getAll();
          if (res.ok) setTeams(res.data || []);
        }
        if (activeAction === 'tools') {
          const res = await api.tool.getAll();
          if (res.ok) setTools(res.data || []);
        }
      } catch (err) {
        console.error('Error loading reference data:', err);
      } finally {
        setLoadingRef(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAction]);

  // ── Coverage models (from DB or fallback) ────────────────────────
  const activeCoverages = useMemo(() => {
    if (coverageModels.length > 0) {
      return coverageModels.map(m => ({ id: m.level || m.code, name: m.name, level: m.level, dbId: m.id }));
    }
    return FALLBACK_COVERAGES;
  }, [coverageModels]);

  // ── Available vehicles for Column A ───────────────────────────────
  const MAX_RENDERED = 500;
  const allActiveVehicles = useMemo(() => {
    let list = vehicles.filter(v => v.status !== 'retired' && !done.some(d => d.id === v.id));
    // For team action: only show vehicles with assigned driver
    if (activeAction === 'team') {
      list = list.filter(v => v.assigned_driver_id);
    }
    // For insurance action: hide any vehicle that already has an active VI on
    // ANY contract (global filter). Aligned with InsuranceVehiclePicker.
    if (activeAction === 'insurance') {
      list = list.filter(v => !globallyInsuredVehicleIds.has(v.id));
    }
    // Search filter
    if (searchA) {
      const q = searchA.toLowerCase();
      list = list.filter(v =>
        v.license_plate?.toLowerCase().includes(q) ||
        v.make?.toLowerCase().includes(q) ||
        v.model?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [vehicles, done, activeAction, searchA, globallyInsuredVehicleIds]);
  // Cap rendered vehicles to prevent DOM overload
  const activeVehicles = useMemo(() => allActiveVehicles.slice(0, MAX_RENDERED), [allActiveVehicles]);
  const hasMore = allActiveVehicles.length > MAX_RENDERED;

  // Unassigned tools for tools action
  const availableTools = useMemo(() => {
    return tools.filter(t => !t.assigned_vehicle_id);
  }, [tools]);

  // ── Selection toggle ──────────────────────────────────────────────
  const toggleVehicle = id => {
    if (activeAction === 'tools') {
      // Single-select for tools
      setSelected(new Set([id]));
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleTool = id => {
    const next = new Set(selectedTools);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedTools(next);
  };

  // ── Can Apply guard ───────────────────────────────────────────────
  const canApply = useMemo(() => {
    if (selected.size === 0) return false;
    if (activeAction === 'site') return !!siteId;
    if (activeAction === 'insurance') return !!contractId;
    if (activeAction === 'driver') {
      const mapped = [...selected].filter(id => driverMap[id]);
      return mapped.length > 0;
    }
    if (activeAction === 'team') return !!teamId;
    if (activeAction === 'tools') return selectedTools.size > 0;
    return false;
  }, [activeAction, selected, siteId, contractId, driverMap, teamId, selectedTools]);

  // ── Apply: move selected → done ───────────────────────────────────
  const handleApply = () => {
    if (!canApply) return;
    const toMove = activeVehicles.filter(v => selected.has(v.id));
    let newDone = [];

    switch (activeAction) {
      case 'site': {
        const site = (loadedSites || sites).find(s => String(s.id) === String(siteId));
        newDone = toMove.map(v => ({
          ...v,
          actionType: 'site',
          detail: site?.site_name || 'Site',
          siteId: Number(siteId),
          assignType,
          effectDate,
        }));
        break;
      }
      case 'insurance': {
        const contract = contracts.find(c => String(c.id) === String(contractId));
        newDone = toMove.map(v => ({
          ...v,
          actionType: 'insurance',
          detail: `${contract?.product_name || contract?.policy_number || 'Contrat'} — ${activeCoverages.find(c => c.id === coverage)?.name || coverage}`,
          contractId: Number(contractId),
          contractOrgId: contract?.org_id,
          coverageId: coverage,
          coverageModelId: activeCoverages.find(c => c.id === coverage)?.dbId || null,
          coverageLevel: activeCoverages.find(c => c.id === coverage)?.level || coverage,
          effectDate,
        }));
        break;
      }
      case 'driver': {
        newDone = toMove
          .filter(v => driverMap[v.id])
          .map(v => {
            const d = drivers.find(dr => String(dr.id) === String(driverMap[v.id]));
            return {
              ...v,
              actionType: 'driver',
              detail: d ? `${d.first_name} ${d.last_name}` : 'Conducteur',
              driverId: Number(driverMap[v.id]),
            };
          });
        // Clean up driver map entries for moved vehicles
        const nextMap = { ...driverMap };
        newDone.forEach(v => delete nextMap[v.id]);
        setDriverMap(nextMap);
        break;
      }
      case 'team': {
        const team = teams.find(t => String(t.id) === String(teamId));
        newDone = toMove.map(v => {
          const d = drivers.find(dr => dr.id === v.assigned_driver_id);
          return {
            ...v,
            actionType: 'team',
            detail: `${team?.name || 'Équipe'} (+ ${d ? d.first_name : 'conducteur'})`,
            teamId: Number(teamId),
            driverId: v.assigned_driver_id,
          };
        });
        break;
      }
      case 'tools': {
        const vehicleId = [...selected][0];
        const vehicle = activeVehicles.find(v => v.id === vehicleId);
        const toolsList = tools.filter(t => selectedTools.has(t.id));
        newDone = toolsList.map(t => ({
          id: `tool-${t.id}-${vehicleId}`,
          actionType: 'tools',
          detail: `${t.tool_name || t.name || 'Outil'} → ${vehicle?.license_plate}`,
          toolId: t.id,
          vehicleId,
          license_plate: vehicle?.license_plate,
          make: vehicle?.make,
          model: vehicle?.model,
        }));
        setSelectedTools(new Set());
        break;
      }
      default:
        break;
    }

    setDone([...newDone, ...done]);
    setSelected(new Set());
  };

  // ── Confirm: send to API ──────────────────────────────────────────
  const handleConfirm = async () => {
    setSending(true);
    setProgress({ current: 0, total: done.length });

    try {
      for (let i = 0; i < done.length; i++) {
        const item = done[i];
        setProgress({ current: i + 1, total: done.length });

        switch (item.actionType) {
          case 'site': {
            // Clear old is_current assignments for this vehicle
            try {
              const oldLocs = await apiRequest(`/vehicle_location?vehicle_id=${item.id}&is_current=1`);
              for (const loc of (oldLocs?.data || [])) {
                await apiRequest(`/vehicle_location/${loc.id}`, {
                  method: 'PUT',
                  body: JSON.stringify({ is_current: 0, removed_at: item.effectDate }),
                });
              }
            } catch { /* best effort */ }
            // Update vehicle's working_site_id
            await api.vehicle.update(item.id, { working_site_id: item.siteId });
            // Create new current assignment
            await apiRequest('/vehicle_location', {
              method: 'POST',
              body: JSON.stringify({
                vehicle_id: item.id,
                site_id: item.siteId,
                assignment_type: item.assignType,
                assigned_at: item.effectDate,
                is_current: 1,
              }),
            });
            break;
          }
          case 'insurance':
            await insuranceModuleAPI.assignVehicle({
              vehicle_id: item.id,
              contract_id: item.contractId,
              assigned_at: item.effectDate,
              is_primary: true,
              coverage_model_id: item.coverageModelId || null,
            });
            break;
          case 'driver':
            await api.vehicle.update(item.id, { assigned_driver_id: item.driverId });
            break;
          case 'team':
            await api.team.addMember(item.teamId, { member_type: 'vehicle', member_id: item.id });
            if (item.driverId) {
              await api.team.addMember(item.teamId, { member_type: 'driver', member_id: item.driverId });
            }
            break;
          case 'tools':
            await api.tool.update(item.toolId, {
              assigned_to_type: 'vehicle',
              assigned_vehicle_id: item.vehicleId,
            });
            break;
          default:
            break;
        }
      }

      onMessage?.({ type: 'success', text: `${done.length} action(s) groupée(s) appliquée(s) avec succès` });
      onSuccess?.();
      // Small delay so the user sees the success message before modal closes
      setTimeout(() => onClose(), 600);
    } catch (err) {
      console.error('Bulk action error:', err);
      onMessage?.({ type: 'error', text: `Erreur lors du traitement (${progress.current}/${progress.total})` });
    } finally {
      setSending(false);
    }
  };

  // ── Reset when switching action ───────────────────────────────────
  const selectAction = (actionId) => {
    setActiveAction(actionId);
    setSelected(new Set());
    setDone([]);
    setSearchA('');
    setSiteId('');
    setAssignType('permanent');
    setEffectDate(new Date().toISOString().split('T')[0]);
    setContractId('');
    setCoverage('tous_risques');
    setDriverMap({});
    setTeamId('');
    setSelectedTools(new Set());
  };

  const actionDef = ACTIONS.find(a => a.id === activeAction);
  const colors = actionDef ? COLOR_MAP[actionDef.color] : null;

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[960px] h-[640px] flex flex-col overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className={`bg-gradient-to-r ${activeAction ? colors.gradient : 'from-amber-500 to-amber-600'} text-white px-5 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            {activeAction && (
              <button onClick={() => setActiveAction(null)} className="text-white/80 hover:text-white">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold">
                {activeAction ? actionDef.label : 'Actions groupées'}
              </h2>
              <p className="text-white/70 text-sm">
                {activeAction ? actionDef.desc : 'Sélectionnez un type d\'action'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        {!activeAction ? (
          /* ── Action Selector Grid ──────────────────────────────── */
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="grid grid-cols-3 gap-4 max-w-[700px]">
              {ACTIONS.map(a => {
                const c = COLOR_MAP[a.color];
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    onClick={() => {
                      if (a.id === 'inspection' && onOpenInspection) {
                        onClose();
                        onOpenInspection();
                      } else {
                        selectAction(a.id);
                      }
                    }}
                    className={`${c.bg} ${c.border} border-2 rounded-xl p-5 text-left hover:shadow-md transition-all hover:scale-[1.02] group`}
                  >
                    <Icon className={`w-8 h-8 ${c.icon} mb-3`} />
                    <h3 className={`font-semibold ${c.text} mb-1`}>{a.label}</h3>
                    <p className="text-xs text-gray-500">{a.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── 3-Column Pit Layout ───────────────────────────────── */
          <div className="flex-1 grid grid-cols-[1fr_240px_1fr] overflow-hidden">

            {/* ── Column A: Vehicle list ─────────────────────────── */}
            <div className="flex flex-col border-r border-gray-200 min-h-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  {activeAction === 'tools' ? 'Véhicule cible' : 'Véhicules'}
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                    {allActiveVehicles.length}
                  </span>
                  {activeAction !== 'tools' && activeVehicles.length > 0 && (
                    <span className="ml-auto flex items-center gap-2">
                      {selected.size > 0 && (
                        <button
                          onClick={() => setSelected(new Set())}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Désélectionner
                        </button>
                      )}
                      <button
                        onClick={() => setSelected(new Set(activeVehicles.map(v => v.id)))}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Tout sélectionner ({activeVehicles.length})
                      </button>
                    </span>
                  )}
                </h3>
                <div className="mt-2 relative">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={searchA}
                    onChange={e => setSearchA(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {hasMore && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    {allActiveVehicles.length} véhicules — affichage limité à {MAX_RENDERED}. Utilisez la recherche.
                  </p>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {activeVehicles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <Check className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">Tous traités</p>
                  </div>
                ) : (
                  activeVehicles.map(v => (
                    <div
                      key={v.id}
                      onClick={() => toggleVehicle(v.id)}
                      className={`flex items-center justify-between p-3 mb-2 rounded-lg border-2 cursor-pointer transition-all ${
                        selected.has(v.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 bg-white'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-sm text-gray-800">{v.license_plate}</span>
                        <span className="text-xs text-gray-500 ml-2">{v.make} {v.model}</span>
                      </div>
                      {activeAction === 'team' && v.assigned_driver_id && (
                        <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                          +conducteur
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── Column B: Action-specific options ──────────────── */}
            <div className="flex flex-col bg-gray-50 border-r border-gray-200 min-h-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Options</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-4">
                {loadingRef ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <>
                    {/* ── Site action ──────────────────────────────── */}
                    {activeAction === 'site' && (
                      <>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Site</label>
                          <select
                            value={siteId}
                            onChange={e => setSiteId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Choisir...</option>
                            {(loadedSites || sites).map(s => (
                              <option key={s.id} value={s.id}>{s.site_name} — {s.city || s.site_code}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Type</label>
                          <div className="space-y-1.5">
                            {['permanent', 'temporary'].map(t => (
                              <div
                                key={t}
                                onClick={() => setAssignType(t)}
                                className={`p-2.5 rounded-lg border-2 cursor-pointer text-sm ${
                                  assignType === t ? 'border-blue-500 bg-blue-50 font-semibold' : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                              >
                                {t === 'permanent' ? 'Permanent' : 'Temporaire'}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Date</label>
                          <input type="date" value={effectDate} onChange={e => setEffectDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      </>
                    )}

                    {/* ── Insurance action ─────────────────────────── */}
                    {activeAction === 'insurance' && (
                      <>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Contrat</label>
                          <select
                            value={contractId}
                            onChange={e => setContractId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Choisir...</option>
                            {contracts.map(c => (
                              <option key={c.id} value={c.id}>{c.product_name} — {c.policy_number}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Couverture</label>
                          {activeCoverages.map(c => (
                            <div
                              key={c.id}
                              onClick={() => setCoverage(c.id)}
                              className={`p-2.5 mb-1.5 rounded-lg border-2 cursor-pointer text-sm ${
                                coverage === c.id ? 'border-blue-500 bg-blue-50 font-semibold' : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              {c.name}
                            </div>
                          ))}
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Date d'effet</label>
                          <input type="date" value={effectDate} onChange={e => setEffectDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      </>
                    )}

                    {/* ── Driver action ────────────────────────────── */}
                    {activeAction === 'driver' && (
                      <>
                        <p className="text-xs text-gray-500">Choisissez un conducteur pour chaque véhicule sélectionné.</p>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {[...selected].map(vId => {
                            const v = vehicles.find(vh => vh.id === vId);
                            if (!v) return null;
                            return (
                              <div key={vId} className="bg-white border border-gray-200 rounded-lg p-2.5">
                                <p className="text-xs font-semibold text-gray-700 mb-1">{v.license_plate}</p>
                                <select
                                  value={driverMap[vId] || ''}
                                  onChange={e => setDriverMap(prev => ({ ...prev, [vId]: e.target.value }))}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                  <option value="">Choisir...</option>
                                  {drivers.filter(d => d.status !== 'inactive').map(d => (
                                    <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                        {selected.size === 0 && (
                          <p className="text-xs text-gray-400 italic text-center mt-4">Sélectionnez des véhicules à gauche</p>
                        )}
                      </>
                    )}

                    {/* ── Team action ──────────────────────────────── */}
                    {activeAction === 'team' && (
                      <>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Équipe</label>
                          <select
                            value={teamId}
                            onChange={e => setTeamId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Choisir...</option>
                            {teams.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-purple-700">
                              Le véhicule ET son conducteur assigné seront ajoutés à l'équipe.
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {/* ── Tools action ─────────────────────────────── */}
                    {activeAction === 'tools' && (
                      <>
                        <p className="text-xs text-gray-500 mb-2">Sélectionnez un véhicule à gauche, puis choisissez les outils à assigner.</p>
                        {selected.size > 0 ? (
                          <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                            {availableTools.length === 0 ? (
                              <p className="text-xs text-gray-400 italic text-center py-4">Aucun outil disponible</p>
                            ) : (
                              availableTools.map(t => (
                                <div
                                  key={t.id}
                                  onClick={() => toggleTool(t.id)}
                                  className={`p-2.5 rounded-lg border-2 cursor-pointer text-sm ${
                                    selectedTools.has(t.id) ? 'border-amber-500 bg-amber-50 font-semibold' : 'border-gray-200 bg-white hover:border-gray-300'
                                  }`}
                                >
                                  {t.tool_name || t.name || `Outil #${t.id}`}
                                </div>
                              ))
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic text-center mt-4">Sélectionnez un véhicule à gauche</p>
                        )}
                      </>
                    )}
                  </>
                )}

              </div>
            </div>

            {/* ── Column C: Done ──────────────────────────────────── */}
            <div className="flex flex-col min-h-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  Traités
                  <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">
                    {done.length}
                  </span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Prêts à confirmer</p>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {done.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ListTodo className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm text-center">
                      Les véhicules traités<br />apparaîtront ici
                    </p>
                  </div>
                ) : (
                  done.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="flex items-center gap-3 p-3 mb-2 rounded-lg bg-green-50 border border-green-200"
                    >
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{item.license_plate}</p>
                        <p className="text-xs text-green-700 truncate">{item.detail}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {done.length > 0
              ? `${done.length} action(s) prête(s) — Cliquez "Terminer" pour confirmer`
              : activeAction
                ? 'Sélectionnez des véhicules puis configurez les options'
                : 'Choisissez un type d\'action ci-dessus'
            }
          </span>
          <div className="flex items-center gap-2">
            {activeAction && selected.size > 0 && (
              <span className="text-sm text-blue-700 font-medium mr-2">{selected.size} sélectionné{selected.size > 1 ? 's' : ''}</span>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            {activeAction && (
              <button
                onClick={handleApply}
                disabled={!canApply}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Appliquer ({selected.size}) <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {activeAction && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={done.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Terminer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Confirmation Modal ────────────────────────────────────── */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-[480px] overflow-hidden">
            <div className="bg-green-600 text-white px-5 py-4">
              <h3 className="text-lg font-semibold">Confirmer les actions groupées</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Actions à exécuter</span>
                  <span className="font-semibold">{done.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Type</span>
                  <span className="font-semibold">{actionDef?.label}</span>
                </div>
              </div>

              {sending && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                    <span className="text-sm text-blue-700 font-medium">
                      {progress.current}/{progress.total} traités...
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-blue-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="max-h-[200px] overflow-y-auto space-y-1.5">
                {done.map((item, idx) => (
                  <div key={item.id || idx} className="flex items-center gap-2 text-sm text-gray-700">
                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <span className="font-medium">{item.license_plate}</span>
                    <span className="text-gray-400">—</span>
                    <span className="text-gray-500 truncate">{item.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Retour
              </button>
              <button
                onClick={handleConfirm}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Confirmer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkActionsModal;
